import { decodeGeminiAudio, calculateAudioLevel } from './audioUtils';
import type {
  RealtimeFunctionCall,
  RealtimeToolContext,
  RealtimeToolRegistry,
} from './realtimeTools';
import { getClientBackendBaseUrl } from '@/lib/backend-endpoint';

export type GeminiTranscriptionSource =
  | 'model_turn_text'
  | 'input_transcription'
  | 'output_transcription';

export interface GeminiRealtimeCallbacks {
  onSetupComplete: () => void;
  onPlayingStateChange: (isPlaying: boolean) => void;
  onOutputAudioLevel: (level: number) => void;
  onTranscription: (text: string, isUser: boolean, source: GeminiTranscriptionSource) => void;
  onUsageUpdate: (usage: {
    totalTokens: number;
    lastDelta: number;
    promptTokensTotal: number;
    outputTokensTotal: number;
    lastPromptDelta: number;
    lastOutputDelta: number;
    raw?: unknown;
  }) => void;
  onError: (error: Error) => void;
  onDisconnect: () => void;
  onReconnecting?: (info: { code: number; reason: string }) => void;
  onReconnected?: () => void;
  onTurnComplete?: () => void;
  onGenerationComplete?: () => void;
  onInterrupted?: () => void;
  onModelAudioChunk?: (float32Data: Float32Array) => void;
  onToolCall?: (name: string, args: Record<string, unknown>, result: Record<string, unknown>) => void;
}

/**
 * Parameters passed as query string to the backend WS proxy.
 * The backend uses these to construct the system prompt server-side.
 */
export interface ProxyParams {
  context_type: string;
  user_id?: string;
  element_id?: string;
  /** Parent skill ID — scopes element_id lookup to the correct skill. */
  skill_id?: string;
  subject?: string;
  quest_title?: string;
  chapter_title?: string;
  lesson_title?: string;
  context_summary?: string;
  voice_name?: string;
  personality_id?: string;
  /** Diagram mode hint for system prompt augmentation. */
  diagram_mode?: string;
  wrong_answers?: string; // JSON-encoded WrongAnswerInfo[]
  review_plan?: string; // JSON-encoded lesson review plan
  current_step_index?: number;
  /** Question text fallback — used when the problem isn't in Supabase (e.g. static JSON problems). */
  question?: string;
  /** JSON-encoded displayed options in the order shown to the student (may differ from DB order due to shuffle). */
  options?: string;
  /** Correct answer for static JSON problems not in Supabase. */
  answer?: string;
  /** Solution explanation for static JSON problems not in Supabase. */
  explanation?: string;
  /** Client-provided prompt for V1 backward compat (prompt is NOT hidden from client in this case). */
  client_prompt?: string;
  /** Langfuse session ID — sent to the backend so it can attach the system prompt to the trace. */
  session_id?: string;
}

export interface GeminiRealtimeOptions {
  callbacks: GeminiRealtimeCallbacks;
  proxyParams: ProxyParams;
  voiceName?: string;
  toolRegistry?: RealtimeToolRegistry;
  toolContext?: RealtimeToolContext;
}

export class GeminiRealtimeWebSocket {
  private ws: WebSocket | null = null;
  private isConnected: boolean = false;
  private isSetupComplete: boolean = false;
  private callbacks: GeminiRealtimeCallbacks;
  private proxyParams: ProxyParams;
  private totalTokenCount: number = 0;
  private totalPromptTokens: number = 0;
  private totalOutputTokens: number = 0;
  private voiceName: string;
  private toolRegistry?: RealtimeToolRegistry;
  private toolContext: RealtimeToolContext;

  // Session resumption
  private resumptionHandle: string | null = null;
  private isReconnecting: boolean = false;
  private intentionalDisconnect: boolean = false;
  private lastUserTranscriptAt: number | null = null;
  private lastUserTranscriptText: string | null = null;

  // Provider info received from backend proxy (injected in setupComplete)
  private _provider?: string;
  private _model?: string;

  // Serialize message processing to preserve ordering of tool calls vs text
  private messageQueue: Promise<void> = Promise.resolve();

  // Audio playback
  private audioContext: AudioContext | null = null;
  private audioQueue: Float32Array[] = [];
  private currentSource: AudioBufferSourceNode | null = null;
  private isSpeaking: boolean = false;
  private nextPlayTime: number = 0; // For gapless scheduled playback
  private scheduledSources: AudioBufferSourceNode[] = []; // Track all scheduled sources
  private lastAudioLevelTime: number = 0; // Throttle visual level updates to ~15fps
  private audioChunkCount: number = 0; // Track total audio chunks received for diagnostics

  constructor(options: GeminiRealtimeOptions) {
    this.callbacks = options.callbacks;
    this.proxyParams = options.proxyParams;
    this.voiceName = options.voiceName ?? 'Aoede';
    this.toolRegistry = options.toolRegistry;
    this.toolContext = options.toolContext ?? { endSession: () => this.disconnect() };
    // Create AudioContext at 24kHz for playback (Gemini output rate)
    this.audioContext = new AudioContext({ sampleRate: 24000 });
    console.log('[GeminiWS] Playback AudioContext created, state:', this.audioContext.state);
  }

  /** Provider info (e.g. 'google-ai-studio' or 'vertex-ai'), set after setupComplete. */
  get provider(): string | undefined {
    return this._provider;
  }

  get model(): string | undefined {
    return this._model;
  }

  /**
   * Build the WebSocket URL to our backend proxy.
   * The backend handles token generation, system prompt construction,
   * and upstream Gemini connection.
   */
  private buildProxyUrl(): string {
    const params = new URLSearchParams();
    params.set('context_type', this.proxyParams.context_type);
    if (this.proxyParams.user_id) params.set('user_id', this.proxyParams.user_id);
    if (this.proxyParams.element_id) params.set('element_id', this.proxyParams.element_id);
    if (this.proxyParams.skill_id) params.set('skill_id', this.proxyParams.skill_id);
    if (this.proxyParams.subject) params.set('subject', this.proxyParams.subject);
    if (this.proxyParams.voice_name) params.set('voice_name', this.proxyParams.voice_name);
    if (this.proxyParams.personality_id) params.set('personality_id', this.proxyParams.personality_id);
    if (this.proxyParams.diagram_mode) params.set('diagram_mode', this.proxyParams.diagram_mode);
    if (this.proxyParams.question) params.set('question', this.proxyParams.question);
    if (this.proxyParams.options) params.set('options', this.proxyParams.options);
    if (this.proxyParams.answer) params.set('answer', this.proxyParams.answer);
    if (this.proxyParams.explanation) params.set('explanation', this.proxyParams.explanation);
    if (this.proxyParams.client_prompt) params.set('client_prompt', this.proxyParams.client_prompt);
    if (this.proxyParams.session_id) params.set('session_id', this.proxyParams.session_id);

    const apiUrl = getClientBackendBaseUrl();
    const url = new URL(apiUrl);
    const protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${url.host}/api/gemini-live?${params.toString()}`;
  }

  async connect(): Promise<void> {
    if (this.ws?.readyState === WebSocket.OPEN) {
      return;
    }

    try {
      const wsUrl = this.buildProxyUrl();
      console.log('[GeminiWS] Connecting to proxy...');
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        if (this.intentionalDisconnect || this.ws?.readyState !== WebSocket.OPEN) {
          return;
        }
        this.isConnected = true;
        this.sendInitialSetup();
      };

      this.ws.onmessage = (event) => {
        if (this.intentionalDisconnect) {
          return;
        }
        // Chain through messageQueue to serialize processing.
        // Without this, concurrent async handlers cause tool calls and text
        // to interleave in the wrong order in the message log.
        this.messageQueue = this.messageQueue.then(async () => {
          try {
            let messageText: string;
            if (event.data instanceof Blob) {
              const arrayBuffer = await event.data.arrayBuffer();
              const bytes = new Uint8Array(arrayBuffer);
              messageText = new TextDecoder('utf-8').decode(bytes);
            } else {
              messageText = event.data;
            }
            await this.handleMessage(messageText);
          } catch (error) {
            console.error('[GeminiWS] Error processing message:', error);
          }
        });
      };

      this.ws.onerror = (error) => {
        if (this.intentionalDisconnect) {
          return;
        }
        console.error('[GeminiWS] WebSocket error:', error);
        this.callbacks.onError(new Error('WebSocket connection error'));
      };

      this.ws.onclose = (event) => {
        console.log('[GeminiWS] Disconnected:', {
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean,
          isSetupComplete: this.isSetupComplete,
        });
        this.isConnected = false;

        // If we intentionally disconnected, just notify
        if (this.intentionalDisconnect) {
          this.callbacks.onDisconnect();
          return;
        }

        // Server-side errors that warrant automatic reconnection
        const reconnectableCodes = new Set([1008, 1011]);
        const shouldReconnect = this.isSetupComplete
          && (this.resumptionHandle || reconnectableCodes.has(event.code));

        if (shouldReconnect) {
          console.log(`[GeminiWS] Connection lost (code: ${event.code}), attempting reconnect`);
          void this.performReconnect({ code: event.code, reason: event.reason });
          return;
        }

        // Report error for non-normal close codes (1000 = normal, 1001 = going away)
        if (event.code !== 1000 && event.code !== 1001) {
          const errorMsg = event.reason || `Connection closed (code: ${event.code})`;
          console.error('[GeminiWS] Connection error:', errorMsg);
          this.callbacks.onError(new Error(errorMsg));
          return;
        }

        this.callbacks.onDisconnect();
      };
    } catch (error) {
      console.error('[GeminiWS] Connection error:', error);
      this.callbacks.onError(error instanceof Error ? error : new Error('Connection failed'));
    }
  }

  /**
   * Send setup message to the backend proxy.
   * The backend injects `model` and `system_instruction` before forwarding to Gemini.
   */
  private sendInitialSetup(): void {
    if (this.intentionalDisconnect || !this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return;
    }
    const setupMessage: Record<string, unknown> = {
      setup: {
        // model and system_instruction are injected by the backend proxy
        generation_config: {
          response_modalities: ['AUDIO'],
          speech_config: {
            voice_config: {
              prebuilt_voice_config: {
                voice_name: this.voiceName,
              },
            },
          },
        },
        // Enable built-in transcription
        input_audio_transcription: {},
        output_audio_transcription: {},
        // Note: proactive_audio is not supported in Gemini 3.1 Live
        // VAD configuration for better voice pickup
        realtime_input_config: {
          automatic_activity_detection: {
            start_of_speech_sensitivity: 'START_SENSITIVITY_HIGH',
            end_of_speech_sensitivity: 'END_SENSITIVITY_LOW',
            prefix_padding_ms: 100,
            silence_duration_ms: 250,
          },
        },
        // Session resumption
        session_resumption: this.resumptionHandle
          ? { handle: this.resumptionHandle }
          : {},
        client_context: {
          quest_title: this.proxyParams.quest_title,
          chapter_title: this.proxyParams.chapter_title,
          lesson_title: this.proxyParams.lesson_title,
          context_summary: this.proxyParams.context_summary,
          wrong_answers: this.proxyParams.wrong_answers,
          review_plan: this.proxyParams.review_plan,
          current_step_index: this.proxyParams.current_step_index,
        },
      },
    };

    // Client always sends AI Studio format; the backend proxy adjusts for Vertex if needed
    const toolDeclarations = this.toolRegistry?.getFunctionDeclarations();
    if (toolDeclarations && toolDeclarations.length > 0) {
      (setupMessage.setup as Record<string, unknown>).tools = [
        { function_declarations: toolDeclarations },
      ];
      console.log('[GeminiWS] Registered tool declarations:', toolDeclarations.map((tool) => tool.name));
    }
    console.log('[GeminiWS] Sending setup to proxy');
    this.ws.send(JSON.stringify(setupMessage));
  }

  sendAudioChunk(base64PcmData: string): void {
    if (!this.isConnected || !this.ws || !this.isSetupComplete || this.ws.readyState !== WebSocket.OPEN) {
      return;
    }

    const message = {
      realtime_input: {
        audio: {
          mime_type: 'audio/pcm',
          data: base64PcmData,
        },
      },
    };

    try {
      this.ws.send(JSON.stringify(message));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      // Race condition during teardown: audio worklet can still emit chunks briefly
      // after socket starts closing. Treat this as expected and stop sending.
      if (errorMessage.includes('CLOSING') || errorMessage.includes('CLOSED')) {
        this.isConnected = false;
        return;
      }
      console.error('[GeminiWS] Error sending audio chunk:', error);
    }
  }

  sendClientContent(text: string, turnComplete: boolean = false): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn('[GeminiWS] Cannot send client content: WebSocket not connected');
      return;
    }

    if (!this.isSetupComplete) {
      console.warn('[GeminiWS] Cannot send client content: Setup not complete');
      return;
    }

    const message = {
      client_content: {
        turns: [
          {
            role: 'user',
            parts: [{ text }],
          },
        ],
        turn_complete: turnComplete,
      },
    };

    console.log(`[GeminiWS] Sending client content (turn_complete: ${turnComplete}):`, text);
    this.ws.send(JSON.stringify(message));
  }

  /**
   * Send text via realtime_input (for mid-session text in Gemini 3.1+).
   * Unlike sendClientContent, this does not interrupt model generation
   * and turn boundaries are inferred from activity detection.
   */
  sendRealtimeText(text: string): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn('[GeminiWS] Cannot send realtime text: WebSocket not connected');
      return;
    }

    if (!this.isSetupComplete) {
      console.warn('[GeminiWS] Cannot send realtime text: Setup not complete');
      return;
    }

    const message = {
      realtime_input: {
        text: text,
      },
    };

    console.log('[GeminiWS] Sending realtime text:', text.slice(0, 100));
    this.ws.send(JSON.stringify(message));
  }

  private async handleMessage(message: string): Promise<void> {
    try {
      const messageData = JSON.parse(message);

      const usageMetadata =
        messageData.usageMetadata ||
        messageData.usage_metadata ||
        messageData.serverContent?.usageMetadata ||
        messageData.serverContent?.usage_metadata ||
        messageData.serverContent?.modelTurn?.usageMetadata ||
        messageData.serverContent?.modelTurn?.usage_metadata;

      if (usageMetadata) {
        const promptTokens = usageMetadata.promptTokenCount ?? usageMetadata.prompt_token_count;
        const outputTokens = usageMetadata.candidatesTokenCount ?? usageMetadata.candidates_token_count;
        const totalTokens = usageMetadata.totalTokenCount ?? usageMetadata.total_token_count;
        const promptDelta = typeof promptTokens === 'number' ? promptTokens : 0;
        const outputDelta = typeof outputTokens === 'number' ? outputTokens : 0;
        const delta =
          typeof totalTokens === 'number'
            ? totalTokens
            : promptDelta + outputDelta;

        if (delta > 0) {
          this.totalTokenCount += delta;
          this.totalPromptTokens += promptDelta;
          this.totalOutputTokens += outputDelta;
          this.callbacks.onUsageUpdate({
            totalTokens: this.totalTokenCount,
            lastDelta: delta,
            promptTokensTotal: this.totalPromptTokens,
            outputTokensTotal: this.totalOutputTokens,
            lastPromptDelta: promptDelta,
            lastOutputDelta: outputDelta,
            raw: usageMetadata,
          });
        }
      }

      // Handle error messages from Gemini
      if (messageData.error) {
        console.error('[GeminiWS] Error from Gemini:', messageData.error);
        this.callbacks.onError(new Error(messageData.error.message || JSON.stringify(messageData.error)));
        return;
      }

      // Handle setup completion
      if (messageData.setupComplete) {
        // Extract proxy metadata (provider info) injected by backend
        if (messageData.proxyMeta?.provider) {
          this._provider = messageData.proxyMeta.provider;
        }
        if (messageData.proxyMeta?.model) {
          this._model = messageData.proxyMeta.model;
        }
        console.log('[GeminiWS] Setup complete, provider/model:', this._provider, this._model);
        this.isSetupComplete = true;
        this.callbacks.onSetupComplete();
        return;
      }

      // Handle session resumption updates — store handle for future reconnects
      const resumptionUpdate = messageData.sessionResumptionUpdate
        ?? messageData.session_resumption_update;
      if (resumptionUpdate) {
        const newHandle = resumptionUpdate.newHandle ?? resumptionUpdate.new_handle;
        if (resumptionUpdate.resumable && newHandle) {
          this.resumptionHandle = newHandle;
        }
      }

      const toolCallMessage = messageData.toolCall || messageData.tool_call;
      const toolCallCancellation = messageData.toolCallCancellation || messageData.tool_call_cancellation;

      if (toolCallMessage?.functionCalls || toolCallMessage?.function_calls) {
        console.log('[GeminiWS] Tool call envelope received');
        await this.handleToolCalls(
          (toolCallMessage.functionCalls || toolCallMessage.function_calls) as RealtimeFunctionCall[]
        );
        return;
      }

      if (toolCallCancellation) {
        console.log('[GeminiWS] Tool call cancelled:', toolCallCancellation);
      }

      // Handle model turn (audio + text transcription)
      if (messageData.serverContent?.modelTurn?.parts) {
        const parts = messageData.serverContent.modelTurn.parts;
        const partLevelToolCalls: RealtimeFunctionCall[] = [];
        let hasAudio = false;
        let hasText = false;

        for (const part of parts) {
          const functionCallPart = part.functionCall || part.function_call;
          if (functionCallPart?.name) {
            partLevelToolCalls.push({
              id: functionCallPart.id,
              name: functionCallPart.name,
              args: functionCallPart.args ?? functionCallPart.arguments,
            });
          }

          // Handle audio data
          if (part.inlineData?.mimeType?.startsWith('audio/pcm')) {
            hasAudio = true;
            const float32Data = decodeGeminiAudio(part.inlineData.data);
            this.playFloat32Audio(float32Data);
            this.callbacks.onModelAudioChunk?.(float32Data);
          }
          // Handle text transcription (output transcription)
          if (part.text) {
            hasText = true;
            this.callbacks.onTranscription(part.text, false, 'model_turn_text');
          }
        }

        // Log when a model turn has text but no audio — helps diagnose missing playback
        if (hasText && !hasAudio) {
          console.warn('[GeminiWS] modelTurn has text but NO audio data (text-only response)');
        }

        if (partLevelToolCalls.length > 0) {
          console.log('[GeminiWS] Part-level function calls received:', partLevelToolCalls.map((c) => c.name));
          await this.handleToolCalls(partLevelToolCalls);
        }
      }

      // Handle input audio transcription (user speech)
      if (messageData.serverContent?.inputTranscription?.text) {
        this.lastUserTranscriptAt = Date.now();
        this.lastUserTranscriptText = messageData.serverContent.inputTranscription.text;
        console.log('[GeminiWS] Input transcription received:', {
          text: messageData.serverContent.inputTranscription.text,
          at: this.lastUserTranscriptAt,
        });
        this.callbacks.onTranscription(messageData.serverContent.inputTranscription.text, true, 'input_transcription');
      }

      // Handle output audio transcription (model speech)
      if (messageData.serverContent?.outputTranscription?.text) {
        this.callbacks.onTranscription(messageData.serverContent.outputTranscription.text, false, 'output_transcription');
      }

      // Handle interruption — stop playing buffered audio immediately
      // and notify listeners so they can reset streaming state (e.g. tool parser)
      if (messageData.serverContent?.interrupted === true) {
        console.log('[GeminiWS] Model interrupted by user');
        this.stopCurrentAudio();
        this.callbacks.onInterrupted?.();
      }

      // Handle turn completion
      if (messageData.serverContent?.turnComplete === true) {
        console.log('[GeminiWS] Turn complete — audioChunks:', this.audioChunkCount,
          'scheduledSources:', this.scheduledSources.length,
          'isSpeaking:', this.isSpeaking,
          'ctxState:', this.audioContext?.state,
          'ctxTime:', this.audioContext?.currentTime?.toFixed(2),
          'msSinceLastUserTranscript:', this.lastUserTranscriptAt ? Date.now() - this.lastUserTranscriptAt : null,
          'lastUserTranscriptText:', this.lastUserTranscriptText);
        this.callbacks.onTurnComplete?.();
      }

      // Handle generation completion
      if (messageData.serverContent?.generationComplete === true) {
        console.log('[GeminiWS] Generation complete', {
          msSinceLastUserTranscript: this.lastUserTranscriptAt ? Date.now() - this.lastUserTranscriptAt : null,
          lastUserTranscriptText: this.lastUserTranscriptText,
        });
        this.callbacks.onGenerationComplete?.();
      }

      // Log other serverContent fields we might be missing
      if (messageData.serverContent) {
        const knownFields = ['modelTurn', 'inputTranscription', 'outputTranscription', 'turnComplete', 'generationComplete', 'interrupted'];
        const otherFields = Object.keys(messageData.serverContent).filter(k => !knownFields.includes(k));
        if (otherFields.length > 0) {
          console.log('[GeminiWS] Other serverContent fields:', {
            otherFields,
            msSinceLastUserTranscript: this.lastUserTranscriptAt ? Date.now() - this.lastUserTranscriptAt : null,
            lastUserTranscriptText: this.lastUserTranscriptText,
            payload: messageData.serverContent,
          });
        }
      }
    } catch (error) {
      console.error('[GeminiWS] Error parsing message:', error);
    }
  }

  private async handleToolCalls(functionCalls: RealtimeFunctionCall[]): Promise<void> {
    if (!this.toolRegistry) {
      console.warn('[GeminiWS] Tool call received but no registry configured');
      return;
    }

    console.log('[GeminiWS] Tool calls received:', functionCalls);

    const responses: Array<{ id?: string; name: string; response: Record<string, unknown> }> = [];
    const postActions: Array<() => void> = [];

    for (const call of functionCalls) {
      if (!call?.name) {
        continue;
      }

      try {
        const result = await this.toolRegistry.execute(call, this.toolContext);
        const resultResponse = result.response ?? { result: 'ok' };
        console.log('[GeminiWS] Tool call executed:', {
          name: call.name,
          id: call.id,
          result: resultResponse,
          skipResponse: result.skipResponse,
        });
        this.callbacks.onToolCall?.(call.name, (call.args ?? {}) as Record<string, unknown>, resultResponse);
        // Only add to responses if not skipped
        if (!result.skipResponse) {
          responses.push({
            id: call.id,
            name: call.name,
            response: resultResponse,
          });
        }
        if (result.afterResponse) {
          postActions.push(result.afterResponse);
        }
      } catch (error) {
        responses.push({
          id: call.id,
          name: call.name,
          response: {
            error: error instanceof Error ? error.message : 'Tool execution failed',
          },
        });
      }
    }

    if (responses.length > 0) {
      this.sendToolResponse(responses);
    }

    if (postActions.length > 0) {
      setTimeout(() => {
        postActions.forEach((action) => action());
      }, 0);
    }
  }

  private sendToolResponse(functionResponses: Array<{ id?: string; name: string; response: Record<string, unknown> }>): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn('[GeminiWS] Cannot send tool response: WebSocket not connected');
      return;
    }

    const message = {
      tool_response: {
        function_responses: functionResponses,
      },
    };

    console.log('[GeminiWS] Sending tool response:', JSON.stringify(message, null, 2));

    try {
      this.ws.send(JSON.stringify(message));
    } catch (error) {
      console.error('[GeminiWS] Error sending tool response:', error);
    }
  }

  private playFloat32Audio(float32Data: Float32Array): void {
    if (!this.audioContext) return;

    this.audioChunkCount++;
    if (this.audioChunkCount === 1) {
      console.log('[GeminiWS] First audio chunk received — AudioContext state:', this.audioContext.state,
        'currentTime:', this.audioContext.currentTime);
    }

    try {
      this.audioQueue.push(float32Data);
      this.playNextInQueue();
    } catch (error) {
      console.error('[GeminiWS] Error processing audio:', error);
    }
  }

  private async playNextInQueue(): Promise<void> {
    if (!this.audioContext || this.audioQueue.length === 0) {
      return;
    }

    if (this.audioContext.state === 'suspended') {
      console.warn('[GeminiWS] Playback AudioContext is SUSPENDED at chunk #' + this.audioChunkCount +
        ', queued:', this.audioQueue.length, ', currentTime:', this.audioContext.currentTime);
    }

    try {
      if (!this.isSpeaking) {
        this.isSpeaking = true;
        this.callbacks.onPlayingStateChange(true);
      }

      // Process all queued audio chunks with scheduled playback for gapless audio
      while (this.audioQueue.length > 0) {
        const float32Data = this.audioQueue.shift()!;

        // Throttle visual audio-level updates to ~15fps to avoid excessive React re-renders
        const now = performance.now();
        if (now - this.lastAudioLevelTime >= 66) {
          this.lastAudioLevelTime = now;
          const level = calculateAudioLevel(float32Data);
          this.callbacks.onOutputAudioLevel(level);
        }

        // Create AudioBuffer from float32 data
        const audioBuffer = this.audioContext.createBuffer(1, float32Data.length, 24000);
        audioBuffer.getChannelData(0).set(float32Data);

        // Create and connect source
        const source = this.audioContext.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(this.audioContext.destination);

        // Schedule playback at exact time for gapless audio
        const currentTime = this.audioContext.currentTime;
        const startTime = Math.max(currentTime, this.nextPlayTime);

        // Calculate when this buffer will end
        const bufferDuration = audioBuffer.length / audioBuffer.sampleRate;
        this.nextPlayTime = startTime + bufferDuration;

        // Track this source for cleanup
        this.scheduledSources.push(source);
        this.currentSource = source;

        // Clean up when this source ends
        source.onended = () => {
          const index = this.scheduledSources.indexOf(source);
          if (index > -1) {
            this.scheduledSources.splice(index, 1);
          }

          // Only mark as not speaking when all sources are done and queue is empty
          if (this.scheduledSources.length === 0 && this.audioQueue.length === 0) {
            this.currentSource = null;
            this.isSpeaking = false;
            this.callbacks.onPlayingStateChange(false);
            // Reset nextPlayTime for next speech segment
            this.nextPlayTime = 0;
          }
        };

        // Start at scheduled time
        source.start(startTime);
      }
    } catch (error) {
      console.error('[GeminiWS] Error playing audio:', error);
      this.currentSource = null;
      this.isSpeaking = false;
      this.callbacks.onPlayingStateChange(false);
      this.nextPlayTime = 0;
    }
  }

  get isModelSpeaking(): boolean {
    return this.isSpeaking;
  }

  stopCurrentAudio(): void {
    // Stop all scheduled sources
    for (const source of this.scheduledSources) {
      try {
        source.stop();
      } catch {
        // Ignore errors if already stopped
      }
    }
    this.scheduledSources = [];
    this.currentSource = null;
    this.isSpeaking = false;
    this.callbacks.onPlayingStateChange(false);
    this.audioQueue = [];
    this.nextPlayTime = 0;
  }

  disconnect(): void {
    console.log('[GeminiWS] Disconnecting...');
    this.intentionalDisconnect = true;
    this.isSetupComplete = false;
    this.stopCurrentAudio();

    if (this.ws) {
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onerror = null;
      this.ws.onclose = null;
      this.ws.close(1000, 'Intentional disconnect');
      this.ws = null;
    }

    this.isConnected = false;
    this.resumptionHandle = null;
    this.isReconnecting = false;

    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
  }

  private async performReconnect(closeInfo?: { code: number; reason: string }): Promise<void> {
    if (this.isReconnecting || this.intentionalDisconnect) return;
    this.isReconnecting = true;
    this.callbacks.onReconnecting?.(closeInfo ?? { code: 0, reason: 'unknown' });

    const maxAttempts = 3;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      if (this.intentionalDisconnect) { this.isReconnecting = false; return; }

      const delay = Math.pow(2, attempt - 1) * 1000; // 1s, 2s, 4s
      console.log(`[GeminiWS] Reconnect attempt ${attempt}/${maxAttempts} in ${delay}ms`);
      await new Promise(resolve => setTimeout(resolve, delay));

      if (this.intentionalDisconnect) { this.isReconnecting = false; return; }

      try {
        // Close old socket
        if (this.ws) {
          try { this.ws.close(); } catch { /* ignore */ }
          this.ws = null;
        }

        // Rebuild output AudioContext
        if (this.audioContext) {
          try { this.audioContext.close(); } catch { /* ignore */ }
        }
        this.audioContext = new AudioContext({ sampleRate: 24000 });
        this.audioChunkCount = 0;
        this.stopCurrentAudio();
        console.log('[GeminiWS] Reconnect: new playback AudioContext, state:', this.audioContext.state);

        // Connect to proxy (backend handles fresh token)
        const wsUrl = this.buildProxyUrl();
        const ws = await this.connectAndWaitSetup(wsUrl);

        if (this.intentionalDisconnect) {
          ws.close();
          this.isReconnecting = false;
          return;
        }

        // Success — install permanent handlers
        this.ws = ws;
        this.isConnected = true;
        this.isSetupComplete = true;
        this.installPermanentHandlers(ws);

        console.log('[GeminiWS] Reconnected successfully');
        this.isReconnecting = false;
        this.callbacks.onReconnected?.();
        return;
      } catch (err) {
        console.error(`[GeminiWS] Reconnect attempt ${attempt} failed:`, err);
      }
    }

    console.error('[GeminiWS] All reconnect attempts failed');
    this.isReconnecting = false;
    this.callbacks.onError(new Error('Connection lost — reconnection failed'));
  }

  private connectAndWaitSetup(wsUrl: string): Promise<WebSocket> {
    return new Promise<WebSocket>((resolve, reject) => {
      const ws = new WebSocket(wsUrl);
      const timeout = setTimeout(() => reject(new Error('Reconnect timeout')), 15_000);

      ws.onopen = () => {
        this.isSetupComplete = false;
        this.isConnected = false;
        this.sendInitialSetup();
      };

      // Temporarily assign ws so sendInitialSetup can use it
      this.ws = ws;

      ws.onmessage = (event) => {
        this.messageQueue = this.messageQueue.then(async () => {
          try {
            let text: string;
            if (event.data instanceof Blob) {
              text = new TextDecoder('utf-8').decode(new Uint8Array(await event.data.arrayBuffer()));
            } else {
              text = event.data;
            }
            const data = JSON.parse(text);
            if (data.setupComplete) {
              clearTimeout(timeout);
              resolve(ws);
              return;
            }
            // Forward non-setup messages
            await this.handleMessage(text);
          } catch (err) {
            console.error('[GeminiWS] Error during reconnect setup:', err);
          }
        });
      };

      ws.onerror = () => { clearTimeout(timeout); reject(new Error('WebSocket error during reconnect')); };
      ws.onclose = () => { clearTimeout(timeout); reject(new Error('WebSocket closed during reconnect')); };
    });
  }

  private installPermanentHandlers(ws: WebSocket): void {
    ws.onmessage = (event) => {
      this.messageQueue = this.messageQueue.then(async () => {
        try {
          let text: string;
          if (event.data instanceof Blob) {
            text = new TextDecoder('utf-8').decode(new Uint8Array(await event.data.arrayBuffer()));
          } else {
            text = event.data;
          }
          await this.handleMessage(text);
        } catch (err) {
          console.error('[GeminiWS] Error processing message:', err);
        }
      });
    };

    ws.onclose = (event) => {
      console.log('[GeminiWS] Disconnected:', {
        code: event.code, reason: event.reason,
        wasClean: event.wasClean, isSetupComplete: this.isSetupComplete,
      });
      this.isConnected = false;

      if (this.intentionalDisconnect) {
        this.callbacks.onDisconnect();
        return;
      }

      const reconnectableCodes = new Set([1008, 1011]);
      const shouldReconnect = this.isSetupComplete
        && (this.resumptionHandle || reconnectableCodes.has(event.code));

      if (shouldReconnect) {
        console.log(`[GeminiWS] Connection lost (code: ${event.code}), attempting reconnect`);
        void this.performReconnect({ code: event.code, reason: event.reason });
        return;
      }
      if (event.code !== 1000 && event.code !== 1001) {
        this.callbacks.onError(new Error(event.reason || `Connection closed (code: ${event.code})`));
        return;
      }
      this.callbacks.onDisconnect();
    };

    ws.onerror = (error) => {
      console.error('[GeminiWS] WebSocket error:', error);
      this.callbacks.onError(new Error('WebSocket connection error'));
    };
  }
}
